import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserPlus, ArrowLeft, CheckCircle2, XCircle, ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function WalkInPhoto({ id }) {
  const [url, setUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const view = async () => {
    try {
      const r = await axios.get(`${API}/academy/walk-in-photo/${id}`, { withCredentials: true, responseType: "blob" });
      setUrl(URL.createObjectURL(r.data));
      setOpen(true);
    } catch (_) {
      toast.error("Could not load photo");
    }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={view} data-testid={`walkin-photo-btn-${id}`}>
        <ImageIcon className="w-3 h-3 mr-1" /> Photo
      </Button>
      {open && url && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <img src={url} alt="walk-in" className="max-h-[85vh] max-w-full rounded-lg" />
        </div>
      )}
    </>
  );
}

export default function AdminWalkIns() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending_followup");
  const [loading, setLoading] = useState(true);

  const load = async (st = status) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academy/admin/walk-ins?status=${st}`, { withCredentials: true });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load walk-ins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const resolve = async (id, newStatus) => {
    try {
      await axios.post(`${API}/academy/admin/walk-ins/${id}/resolve`, { status: newStatus }, { withCredentials: true });
      toast.success(newStatus === "resolved" ? "Marked followed up" : "Dismissed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold">Walk-in Follow-ups</h1>
          </div>
          <Link to="/admin" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm" data-testid="walkins-back">
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
        </div>
        <p className="text-sm text-slate-400 mb-4">Players who turned up without registering. Follow up to register &amp; bill them.</p>

        <div className="flex gap-2 mb-4">
          {[["pending_followup", "To follow up"], ["resolved", "Followed up"], ["dismissed", "Dismissed"], ["all", "All"]].map(([val, label]) => (
            <Button key={val} size="sm" variant={status === val ? "default" : "outline"}
              className={status === val ? "bg-purple-600 hover:bg-purple-500" : "border-slate-600"}
              onClick={() => { setStatus(val); load(val); }} data-testid={`walkins-filter-${val}`}>
              {label}
            </Button>
          ))}
        </div>

        {loading ? (
          <p className="text-slate-400" data-testid="walkins-loading">Loading…</p>
        ) : rows.length === 0 ? (
          <Card className="bg-slate-900/40 border-slate-700"><CardContent className="py-10 text-center text-slate-400" data-testid="walkins-empty">
            No walk-ins here.
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <Card key={r.id} className="bg-slate-900/40 border-slate-700" data-testid={`walkin-row-${r.id}`}>
                <CardContent className="py-4 flex flex-wrap items-center gap-3 justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{r.player_name}</span>
                      {r.age_group && <Badge className="bg-slate-700 text-slate-200">{r.age_group}</Badge>}
                      <Badge className={
                        r.status === "resolved" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                        : r.status === "dismissed" ? "bg-slate-600/30 text-slate-300"
                        : "bg-amber-500/20 text-amber-300 border-amber-500/40"
                      }>{r.status.replace("_", " ")}</Badge>
                    </div>
                    {r.notes && <p className="text-xs text-slate-400 mt-1">{r.notes}</p>}
                    <p className="text-xs text-slate-500 mt-1">
                      {r.session_date ? `Session ${r.session_date} · ` : ""}logged by {r.recorded_by_name || "coach"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.has_photo && <WalkInPhoto id={r.id} />}
                    {r.status === "pending_followup" && (
                      <>
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500" onClick={() => resolve(r.id, "resolved")} data-testid={`walkin-resolve-${r.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Followed up
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={() => resolve(r.id, "dismissed")} data-testid={`walkin-dismiss-${r.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Dismiss
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
